import type { RrwebEvent } from '@belvdr/shared'
import { uuidv7 } from './uuid.js'

/**
 * Enregistreur d'Extraits du Mode erreurs (voir `CONTEXT.md`), indépendant de rrweb : il reçoit
 * les événements de l'enregistrement de la page et les garde dans un tampon en mémoire, dont rien
 * ne sort tant qu'aucune Exception ne survient.
 *
 * Le tampon est une suite de segments, chacun ouvert par un full snapshot (un événement Meta suivi
 * de l'image de la page) : un full snapshot est forcé toutes les 15 s, et les segments ouverts il
 * y a plus de 60 s sont oubliés. Le tampon couvre donc entre 45 et 60 s, et commence toujours par
 * une image complète, d'où le lecteur peut partir. Il est aussi borné en octets.
 *
 * À une Exception, le tampon est figé et l'enregistrement continue 10 s ; une autre Exception
 * pendant ces 10 s prolonge le même Extrait jusqu'à 10 s après elle. L'Extrait n'est livré que si
 * l'API a répondu `send` pour au moins une de ses Exceptions ; il est abandonné si elles ont
 * toutes reçu `skip` (Issue ignorée).
 */

/** Temps couvert avant l'Exception, au plus (cf. PRD). */
export const EXTRAIT_BEFORE_MS = 60_000
/** Temps enregistré après l'Exception (la dernière, pour un Extrait prolongé). */
export const EXTRAIT_AFTER_MS = 10_000
/** Intervalle des full snapshots forcés : le tampon garde au moins 60 s moins cet intervalle. */
const CHECKOUT_MS = 15_000
/** Extraits au plus par chargement de page (cf. PRD). */
export const MAX_EXTRAITS_PER_PAGE = 5
/** Taille maximale du tampon (JSON) : au-delà, les segments les plus anciens sont oubliés. */
const DEFAULT_MAX_BYTES = 8 * 1024 * 1024

/** Type rrweb de l'événement qui ouvre un full snapshot (`EventType.Meta`). */
const META = 4

export type Extrait = {
  id: string
  /** Événements rrweb, du full snapshot qui ouvre le tampon figé à 10 s après l'Exception. */
  events: RrwebEvent[]
}

/** Réponse de l'API pour l'Extrait d'une Exception : l'envoyer, ou non (Issue ignorée). */
export type ExtraitDecision = 'send' | 'skip'

export type ExtraitRecorder = {
  /** Un événement de l'enregistrement de la page. */
  push(event: RrwebEvent): void
  /**
   * Une Exception à l'instant `now` : l'identifiant de son Extrait (celui en cours, prolongé, ou
   * un nouveau), ou `null` sans Extrait (aucune image encore, limite de la page atteinte,
   * enregistreur arrêté). Chaque Exception d'un Extrait attend sa réponse (`decide`).
   */
  capture(now: number): string | null
  /** La réponse de l'API pour une Exception de l'Extrait `id`. */
  decide(id: string, decision: ExtraitDecision): void
  /**
   * Arrête l'enregistreur. Les Extraits en cours ou en attente de réponse sont livrés au mieux,
   * tels quels, sauf ceux dont toutes les Exceptions ont reçu `skip`.
   */
  stop(): void
}

type Segment = { start: number; events: RrwebEvent[]; bytes: number }

type Pending = {
  id: string
  events: RrwebEvent[]
  /** Fin de l'enregistrement de l'Extrait (10 s après sa dernière Exception). */
  end: number
  timer: ReturnType<typeof setTimeout>
  /** L'enregistrement est terminé : reste la réponse de l'API. */
  ended: boolean
  /** Exceptions encore sans réponse. */
  awaiting: number
  /** Au moins une Exception a reçu `send`. */
  send: boolean
}

export function createExtraitRecorder(options: {
  /** Force un full snapshot (rrweb `record.takeFullSnapshot`). */
  takeFullSnapshot: () => void
  /** Un Extrait terminé, à envoyer. */
  deliver: (extrait: Extrait) => void
  maxBytes?: number
}): ExtraitRecorder {
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES
  const segments: Segment[] = []
  let bytes = 0
  /** Extrait en cours d'enregistrement. */
  let ongoing: Pending | null = null
  /** Extraits en cours ou terminés, pas encore livrés ni abandonnés. */
  const pending = new Map<string, Pending>()
  let produced = 0
  let stopped = false
  const checkout = setInterval(() => options.takeFullSnapshot(), CHECKOUT_MS)

  function dropOldest() {
    const dropped = segments.shift()
    if (dropped) bytes -= dropped.bytes
  }

  /** Oublie ce que le tampon n'a plus à couvrir, en gardant toujours le segment en cours. */
  function trim(now: number) {
    while (segments.length > 1 && segments[0]!.start < now - EXTRAIT_BEFORE_MS) dropOldest()
    while (segments.length > 1 && bytes > maxBytes) dropOldest()
  }

  /** Livre l'Extrait, ou l'abandonne, une fois enregistré et la réponse de l'API connue. */
  function settle(extrait: Pending) {
    if (!extrait.ended) return
    if (extrait.send) {
      pending.delete(extrait.id)
      options.deliver({ id: extrait.id, events: extrait.events })
    } else if (extrait.awaiting === 0) {
      pending.delete(extrait.id)
    }
  }

  function end(extrait: Pending) {
    clearTimeout(extrait.timer)
    extrait.ended = true
    if (ongoing === extrait) ongoing = null
    settle(extrait)
  }

  return {
    push(event) {
      if (stopped) return
      if (event.type === META) segments.push({ start: event.timestamp, events: [], bytes: 0 })
      const current = segments.at(-1)
      // Avant le premier full snapshot, un événement ne peut pas être rejoué : ignoré.
      if (!current) return
      const size = JSON.stringify(event).length
      current.events.push(event)
      current.bytes += size
      bytes += size
      if (ongoing && event.timestamp <= ongoing.end) ongoing.events.push(event)
      trim(event.timestamp)
    },

    capture(now) {
      if (stopped) return null
      const current = ongoing
      if (current) {
        // Exception rapprochée : le même Extrait continue jusqu'à 10 s après elle.
        current.awaiting++
        current.end = Math.max(current.end, now + EXTRAIT_AFTER_MS)
        clearTimeout(current.timer)
        current.timer = setTimeout(() => end(current), current.end - now)
        return current.id
      }
      if (produced >= MAX_EXTRAITS_PER_PAGE) return null
      trim(now)
      if (segments.length === 0) return null
      produced++
      const extrait: Pending = {
        id: uuidv7(now),
        events: segments.flatMap((segment) => segment.events),
        end: now + EXTRAIT_AFTER_MS,
        timer: setTimeout(() => end(extrait), EXTRAIT_AFTER_MS),
        ended: false,
        awaiting: 1,
        send: false,
      }
      ongoing = extrait
      pending.set(extrait.id, extrait)
      return extrait.id
    },

    decide(id, decision) {
      const extrait = pending.get(id)
      if (!extrait) return
      extrait.awaiting = Math.max(0, extrait.awaiting - 1)
      if (decision === 'send') extrait.send = true
      settle(extrait)
    },

    stop() {
      if (stopped) return
      stopped = true
      clearInterval(checkout)
      // Page qui se ferme : sans réponse de l'API, l'Extrait part au mieux.
      for (const extrait of [...pending.values()]) {
        clearTimeout(extrait.timer)
        if (extrait.awaiting > 0) extrait.send = true
        extrait.ended = true
        settle(extrait)
      }
      ongoing = null
      segments.length = 0
      bytes = 0
    },
  }
}
